if (!customElements.get("media-gallery-grid")) {
  customElements.define(
    "media-gallery-grid",
    class MediaGalleryGrid extends HTMLElement {
      constructor() {
        super();
        this.splide = null;
        this.zoomListenersAbort = null;
        this.visibleCount = Number(this.dataset.initial || 8);
        this.loadStep = Number(this.dataset.step || 8);
        this.desktopQuery = window.matchMedia("(min-width: 1280px)");
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onLoadMore = this.onLoadMore.bind(this);
        this.onGalleryScroll = this.onGalleryScroll.bind(this);
      }

      connectedCallback() {
        this.viewport = this.querySelector(".pdp-grid-gallery__viewport");
        this.loadMoreButton = this.querySelector("[data-load-more]");
        this.actions = this.querySelector(".pdp-grid-gallery__actions");
        this.items = Array.from(this.querySelectorAll(".pdp-grid-gallery__item"));
        this.visibleCount = Number(this.dataset.initial || 8);

        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener("click", this.onLoadMore);
        }

        this.querySelectorAll(".pdp-grid-gallery__item--video .video-wrapper").forEach((wrapper) => {
          wrapper.addEventListener("click", () => this.toggleVideo(wrapper));
        });

        this.bindImageFades();
        this.autoplayVideos();
        this.applyVisibility();
        this.initForViewport();
        this.enableInlineZoom();
        this.bindScrollLoad();

        if (this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener("change", this.onBreakpointChange);
        } else {
          this.desktopQuery.addListener(this.onBreakpointChange);
        }
      }

      disconnectedCallback() {
        if (this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener("change", this.onBreakpointChange);
        } else if (this.desktopQuery.removeListener) {
          this.desktopQuery.removeListener(this.onBreakpointChange);
        }
        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener("click", this.onLoadMore);
        }
        this.teardownInlineZoom();
        this.unbindScrollLoad();
        this.destroySplide();
      }

      onBreakpointChange() {
        this.clearInlineZoom();
        this.applyVisibility();
        this.initForViewport();
        this.enableInlineZoom();
        this.bindScrollLoad();
      }

      isDesktop() {
        return this.desktopQuery.matches;
      }

      isMobileCarousel() {
        return !this.isDesktop();
      }

      hasMoreImages() {
        return this.isDesktop() && this.visibleCount < this.items.length;
      }

      getScrollRoot() {
        return this.closest(".product__media-wrapper") || this;
      }

      bindScrollLoad() {
        this.unbindScrollLoad();
        if (!this.isDesktop()) return;
        this.scrollRoot = this.getScrollRoot();
        this.scrollRoot.addEventListener("scroll", this.onGalleryScroll, { passive: true });
        this.maybeLoadMoreFromScroll();
      }

      unbindScrollLoad() {
        if (this.scrollRoot) {
          this.scrollRoot.removeEventListener("scroll", this.onGalleryScroll);
        }
        this.scrollRoot = null;
      }

      onGalleryScroll() {
        this.maybeLoadMoreFromScroll();
      }

      maybeLoadMoreFromScroll() {
        if (!this.isDesktop() || this._loadingMore) return;
        if (this.visibleCount >= this.items.length) return;

        const root = this.scrollRoot || this.getScrollRoot();
        if (!root) return;

        const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;
        if (remaining > 320) return;

        this._loadingMore = true;
        this.onLoadMore();
        requestAnimationFrame(() => {
          this._loadingMore = false;
          this.maybeLoadMoreFromScroll();
        });
      }

      updateLoadMoreState() {
        const show = this.hasMoreImages();
        this.classList.toggle("has-more", show);

        if (this.actions) {
          this.actions.hidden = !show;
          this.actions.classList.toggle("is-hidden", !show);
        }

        if (this.loadMoreButton) {
          this.loadMoreButton.hidden = !show;
        }
      }

      bindImageFades() {
        this.querySelectorAll(".pdp-grid-gallery__image").forEach((img) => {
          const markLoaded = () => img.classList.add("is-loaded");
          if (img.complete && img.naturalWidth > 0) {
            markLoaded();
          } else {
            img.addEventListener("load", markLoaded, { once: true });
            img.addEventListener("error", markLoaded, { once: true });
          }
        });

        this.querySelectorAll(".pdp-grid-gallery__item--video").forEach((item) => {
          const video = item.querySelector(".pdp-grid-gallery__video");
          if (!video) return;

          const markReady = () => {
            video.classList.add("is-loaded");
            item.classList.add("is-ready");
          };

          if (video.readyState >= 2 || video.poster) {
            if (video.poster) markReady();
          }

          video.addEventListener("loadeddata", markReady, { once: true });
          video.addEventListener("error", markReady, { once: true });
        });
      }

      autoplayVideos() {
        this.querySelectorAll(".pdp-grid-gallery__video").forEach((video) => {
          const item = video.closest(".pdp-grid-gallery__item--video");
          video.muted = true;
          video.playsInline = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.then === "function") {
            playPromise
              .then(() => {
                if (item) item.classList.add("is-playing");
              })
              .catch(() => {});
          } else if (!video.paused && item) {
            item.classList.add("is-playing");
          }
        });
      }

      applyVisibility({ animate = false, animateFrom = null } = {}) {
        const desktop = this.isDesktop();

        this.items.forEach((item, index) => {
          if (!desktop) {
            item.classList.remove("is-collapsed", "is-fading-in");
            item.style.removeProperty("animation-delay");
            return;
          }

          const shouldShow = index < this.visibleCount;
          const wasCollapsed = item.classList.contains("is-collapsed");

          if (shouldShow) {
            item.classList.remove("is-collapsed");
            const shouldAnimate =
              animate && wasCollapsed && (animateFrom === null || index >= animateFrom);
            if (shouldAnimate) {
              item.classList.add("is-fading-in");
              item.style.animationDelay = `${Math.max(0, index - animateFrom) * 45}ms`;
              const clearFade = () => {
                item.classList.remove("is-fading-in");
                item.style.removeProperty("animation-delay");
                item.removeEventListener("animationend", clearFade);
              };
              item.addEventListener("animationend", clearFade);
            }
          } else {
            item.classList.add("is-collapsed");
            item.classList.remove("is-fading-in");
            item.style.removeProperty("animation-delay");
          }
        });

        this.updateLoadMoreState();
      }

      onLoadMore() {
        if (!this.isDesktop() || this.visibleCount >= this.items.length) {
          this.updateLoadMoreState();
          return;
        }

        const previousCount = this.visibleCount;
        this.visibleCount = Math.min(this.visibleCount + this.loadStep, this.items.length);

        this.applyVisibility({ animate: true, animateFrom: previousCount });
        this.bindImageFades();
        this.autoplayVideos();
        this.updateLoadMoreState();
      }

      setSplideDragEnabled(enabled) {
        const drag = this.splide?.Components?.Drag;
        if (!drag || typeof drag.disable !== "function") return;
        // Splide Drag has no enable() — disable(false) re-enables swiping.
        drag.disable(!enabled);
      }

      cancelSplideDrag() {
        if (!this.splide) return;
        const { Move, Scroll, Drag } = this.splide.Components || {};
        if (Drag && typeof Drag.disable === "function") Drag.disable(true);
        Scroll?.cancel?.();
        Move?.cancel?.();
        try {
          if (Move?.toPosition && Move?.translate) {
            Move.translate(Move.toPosition(this.splide.index));
          }
        } catch (e) {
          /* ignore */
        }
        // Splide state: DRAGGING = 6, IDLE = 3
        if (this.splide.state?.is?.(6) && this.splide.state?.set) {
          this.splide.state.set(3);
        }
      }

      silenceSplideNavigation() {
        const ctrl = this.splide?.Components?.Controller;
        const scroll = this.splide?.Components?.Scroll;
        if (!ctrl || this._splideNavSilenced) return;
        this._splideNavSilenced = true;
        this._splideGo = ctrl.go;
        this._splideScroll = scroll?.scroll;
        ctrl.go = () => {};
        if (scroll) scroll.scroll = () => {};
        queueMicrotask(() => {
          if (ctrl && this._splideGo) ctrl.go = this._splideGo;
          if (scroll && this._splideScroll) scroll.scroll = this._splideScroll;
          this._splideGo = null;
          this._splideScroll = null;
          this._splideNavSilenced = false;
        });
      }

      isZoomInteractionLocked() {
        if (this.pinchSession || this.touchPanSession?.moved) return true;
        if (this.zoomGestureEl?.classList?.contains("is-zoomed")) return true;
        return Boolean(this.querySelector("[data-image-zoom].is-zoomed"));
      }

      syncSplideDragToZoom() {
        if (!this.isMobileCarousel()) {
          this.classList.remove("is-zoom-interacting");
          return;
        }
        const locked = this.isZoomInteractionLocked();
        this.classList.toggle("is-zoom-interacting", locked);
        this.setSplideDragEnabled(!locked);
      }

      blockSplideTouch(event, { allowSplideCleanup = false } = {}) {
        event.preventDefault();
        // stopImmediatePropagation prevents Splide (capture on track) from seeing the event.
        // On touchend after a 1-finger drag already started, skip that so Splide can unbind
        // its move/end listeners — navigation is silenced separately.
        if (!allowSplideCleanup) {
          event.stopImmediatePropagation();
        }
      }

      snapScaleIfNearRest(scale) {
        // If the customer nearly pinched out, snap fully so carousel swipe works.
        return scale <= 1.15 ? 1 : scale;
      }

      getZoomScale(element) {
        return parseFloat(element?.dataset?.currentScale || "1", 10) || 1;
      }

      getZoomMaxScale(element) {
        return Math.max(1.5, parseFloat(element?.dataset?.scale || "2.5", 10) || 2.5);
      }

      clampZoomPan(element, scaleNum, panX, panY) {
        const w = element.offsetWidth;
        const h = element.offsetHeight;
        const maxX = (w * (scaleNum - 1)) / 2;
        const maxY = (h * (scaleNum - 1)) / 2;
        return {
          x: Math.min(maxX, Math.max(-maxX, panX)),
          y: Math.min(maxY, Math.max(-maxY, panY)),
        };
      }

      getTouchDistance(t1, t2) {
        return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      }

      focalPanFromPoint(clientX, clientY, element, scaleNum) {
        const rect = element.getBoundingClientRect();
        const focalX = clientX - rect.left - element.offsetWidth / 2;
        const focalY = clientY - rect.top - element.offsetHeight / 2;
        return this.clampZoomPan(
          element,
          scaleNum,
          -(scaleNum - 1) * focalX,
          -(scaleNum - 1) * focalY
        );
      }

      getPinchMidpoint(t1, t2) {
        return {
          x: (t1.clientX + t2.clientX) / 2,
          y: (t1.clientY + t2.clientY) / 2,
        };
      }

      panForScaleChange(element, focalClientX, focalClientY, prevScale, nextScale, prevPan) {
        const rect = element.getBoundingClientRect();
        const focalX = focalClientX - rect.left - rect.width / 2;
        const focalY = focalClientY - rect.top - rect.height / 2;
        if (prevScale <= 0) return { ...prevPan };
        const ratio = nextScale / prevScale;
        return {
          x: prevPan.x - focalX * (ratio - 1),
          y: prevPan.y - focalY * (ratio - 1),
        };
      }

      applyInlineTransform(element, image, scale, pan) {
        image.style.transformOrigin = "50% 50%";
        image.style.transform = `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`;
      }

      setGestureState(element, image, isGesturing) {
        element.classList.toggle("pdp-grid-gallery__zoom--gesturing", isGesturing);
      }

      resetZoomElement(element) {
        element.classList.remove("is-zoomed", "pdp-grid-gallery__zoom--panning", "pdp-grid-gallery__zoom--gesturing");
        element.dataset.currentScale = "1";
        element.bbZoomPan = { x: 0, y: 0 };
        const image = element.querySelector(".pdp-grid-gallery__image");
        if (image) {
          image.style.transform = "";
          image.style.transformOrigin = "";
        }
      }

      clearInlineZoom() {
        this.pinchSession = null;
        this.touchPanSession = null;
        this.zoomGestureEl = null;
        this.querySelectorAll("[data-image-zoom]").forEach((el) => this.resetZoomElement(el));
        this.syncSplideDragToZoom();
      }

      teardownInlineZoom() {
        if (this.zoomListenersAbort) {
          this.zoomListenersAbort.abort();
          this.zoomListenersAbort = null;
        }
        this.pinchSession = null;
        this.touchPanSession = null;
        this.zoomGestureEl = null;
        this.classList.remove("is-zoom-interacting");
      }

      enableInlineZoom() {
        this.teardownInlineZoom();
        this.zoomListenersAbort = new AbortController();
        const { signal } = this.zoomListenersAbort;

        this.pinchSession = null;
        this.touchPanSession = null;
        this.zoomGestureEl = null;
        this.lastZoomTouchEnd = 0;
        this.lastZoomTouchX = 0;
        this.lastZoomTouchY = 0;

        const setZoomState = (element, scale, pan, { gesturing = false } = {}) => {
          const image = element.querySelector(".pdp-grid-gallery__image");
          if (!image) return 1;

          const nextScale = gesturing ? scale : this.snapScaleIfNearRest(scale);
          const nextPan = nextScale <= 1 ? { x: 0, y: 0 } : pan;
          const clampedPan = this.clampZoomPan(element, nextScale, nextPan.x, nextPan.y);
          element.dataset.currentScale = String(nextScale);
          element.bbZoomPan = clampedPan;
          const isZoomed = nextScale > 1.02;
          element.classList.toggle("is-zoomed", isZoomed);
          this.setGestureState(element, image, gesturing);
          if (nextScale <= 1) {
            this.resetZoomElement(element);
          } else {
            this.applyInlineTransform(element, image, nextScale, clampedPan);
          }
          this.syncSplideDragToZoom();
          return nextScale;
        };

        const beginPinchSession = (element, t1, t2) => {
          this.touchPanSession = null;
          this.zoomGestureEl = element;
          const midpoint = this.getPinchMidpoint(t1, t2);
          this.pinchSession = {
            element,
            lastDistance: this.getTouchDistance(t1, t2),
            scale: this.getZoomScale(element),
            pan: { ...(element.bbZoomPan || { x: 0, y: 0 }) },
            lastMidX: midpoint.x,
            lastMidY: midpoint.y,
          };
          const image = element.querySelector(".pdp-grid-gallery__image");
          if (image) this.setGestureState(element, image, true);
          this.cancelSplideDrag();
          this.syncSplideDragToZoom();
        };

        const resolveZoomElement = (event) => {
          const fromTarget = event.target?.closest?.("[data-image-zoom]");
          if (fromTarget && this.contains(fromTarget)) return fromTarget;
          return this.zoomGestureEl;
        };

        this.querySelectorAll("[data-image-zoom]").forEach((element) => {
          const image = element.querySelector(".pdp-grid-gallery__image");
          if (!image) return;

          const maxScale = this.getZoomMaxScale(element);
          element.bbZoomPan = { x: 0, y: 0 };
          element.dataset.currentScale = "1";

          element.addEventListener(
            "click",
            (event) => {
              if (!this.isDesktop()) return;
              event.preventDefault();

              if (this.getZoomScale(element) > 1.02) {
                this.resetZoomElement(element);
                return;
              }

              this.clearInlineZoom();
              const pan = this.focalPanFromPoint(event.clientX, event.clientY, element, maxScale);
              setZoomState(element, maxScale, pan);
            },
            { signal }
          );

          element.addEventListener(
            "mousemove",
            (event) => {
              if (!this.isDesktop() || this.getZoomScale(element) <= 1.02) return;
              const rect = element.getBoundingClientRect();
              const offsetX = event.clientX - rect.left;
              const offsetY = event.clientY - rect.top;
              const xPercent = Math.min(Math.max((offsetX / element.offsetWidth) * 100, 0), 100);
              const yPercent = Math.min(Math.max((offsetY / element.offsetHeight) * 100, 0), 100);
              image.style.transformOrigin = `${xPercent}% ${yPercent}%`;
            },
            { signal }
          );
        });

        // Capture on viewport (parent of splide__track) so we run BEFORE Splide's
        // capture-phase drag handlers and can block swipe during pinch/zoom.
        if (this.viewport) {
          this.viewport.addEventListener(
            "touchstart",
            (e) => {
              if (!this.isMobileCarousel()) return;

              const element = resolveZoomElement(e);
              if (!element) return;

              if (e.touches.length >= 2) {
                this.blockSplideTouch(e);
                beginPinchSession(element, e.touches[0], e.touches[1]);
                return;
              }

              if (e.touches.length === 1 && this.getZoomScale(element) > 1.02) {
                this.blockSplideTouch(e);
                this.cancelSplideDrag();
                this.zoomGestureEl = element;
                const t = e.touches[0];
                this.touchPanSession = {
                  element,
                  startX: t.clientX,
                  startY: t.clientY,
                  startPanX: element.bbZoomPan?.x || 0,
                  startPanY: element.bbZoomPan?.y || 0,
                  moved: false,
                };
                this.syncSplideDragToZoom();
              }
            },
            { signal, passive: false, capture: true }
          );

          this.viewport.addEventListener(
            "touchmove",
            (e) => {
              if (!this.isMobileCarousel()) return;

              if (e.touches.length >= 2) {
                const element =
                  this.pinchSession?.element || resolveZoomElement(e) || this.zoomGestureEl;
                if (!element) return;

                this.blockSplideTouch(e);
                if (!this.pinchSession || this.pinchSession.element !== element) {
                  beginPinchSession(element, e.touches[0], e.touches[1]);
                }

                const pinchSession = this.pinchSession;
                if (!pinchSession || pinchSession.lastDistance <= 0) return;

                const t1 = e.touches[0];
                const t2 = e.touches[1];
                const distance = this.getTouchDistance(t1, t2);
                const midpoint = this.getPinchMidpoint(t1, t2);
                const maxScale = this.getZoomMaxScale(element);
                const scaleFactor = distance / pinchSession.lastDistance;
                let nextScale = Math.min(
                  maxScale,
                  Math.max(1, pinchSession.scale * scaleFactor)
                );

                let nextPan = this.panForScaleChange(
                  element,
                  midpoint.x,
                  midpoint.y,
                  pinchSession.scale,
                  nextScale,
                  pinchSession.pan
                );
                nextPan = {
                  x: nextPan.x + (midpoint.x - pinchSession.lastMidX),
                  y: nextPan.y + (midpoint.y - pinchSession.lastMidY),
                };

                pinchSession.scale = nextScale;
                pinchSession.lastDistance = distance;
                pinchSession.lastMidX = midpoint.x;
                pinchSession.lastMidY = midpoint.y;

                const clampedPan = this.clampZoomPan(element, nextScale, nextPan.x, nextPan.y);
                pinchSession.pan = clampedPan;
                setZoomState(element, nextScale, clampedPan, { gesturing: true });
                return;
              }

              const touchPanSession = this.touchPanSession;
              if (!touchPanSession) return;
              const element = touchPanSession.element;
              if (!element || this.getZoomScale(element) <= 1.02) return;
              if (e.touches.length !== 1) return;

              this.blockSplideTouch(e);

              const t = e.touches[0];
              const dx = t.clientX - touchPanSession.startX;
              const dy = t.clientY - touchPanSession.startY;
              if (!touchPanSession.moved && Math.hypot(dx, dy) < 8) return;

              if (!touchPanSession.moved) {
                touchPanSession.moved = true;
                element.classList.add("pdp-grid-gallery__zoom--panning");
                const image = element.querySelector(".pdp-grid-gallery__image");
                if (image) this.setGestureState(element, image, true);
                this.lastZoomTouchEnd = 0;
                this.syncSplideDragToZoom();
              }

              const scale = this.getZoomScale(element);
              const pan = this.clampZoomPan(
                element,
                scale,
                touchPanSession.startPanX + dx,
                touchPanSession.startPanY + dy
              );
              element.bbZoomPan = pan;
              const image = element.querySelector(".pdp-grid-gallery__image");
              if (image) this.applyInlineTransform(element, image, scale, pan);
            },
            { signal, passive: false, capture: true }
          );

          this.viewport.addEventListener(
            "touchend",
            (e) => {
              if (!this.isMobileCarousel()) return;

              const wasPinching = Boolean(this.pinchSession);
              const wasZoomPan = Boolean(this.touchPanSession?.moved);
              const zoomEl = this.zoomGestureEl || this.touchPanSession?.element || this.pinchSession?.element;
              const wasZoomedTouch = Boolean(zoomEl && this.getZoomScale(zoomEl) > 1.02);
              const needsBlock = wasPinching || wasZoomPan || wasZoomedTouch || this.isZoomInteractionLocked();

              if (needsBlock) {
                // After pinch, Splide may already have drag listeners from the first finger.
                // Let touchend reach Splide so it unbinds, but silence go()/scroll().
                const allowSplideCleanup = wasPinching;
                if (allowSplideCleanup) {
                  this.cancelSplideDrag();
                  this.silenceSplideNavigation();
                }
                this.blockSplideTouch(e, { allowSplideCleanup });
              }

              let handedOffToPan = false;
              const element =
                this.pinchSession?.element || this.touchPanSession?.element || resolveZoomElement(e);

              if (this.pinchSession) {
                if (e.touches.length >= 2) return;

                const finalScale = this.pinchSession.scale;
                const finalPan = { ...this.pinchSession.pan };
                const pinchEl = this.pinchSession.element;
                this.pinchSession = null;
                const snappedScale = setZoomState(pinchEl, finalScale, finalPan, {
                  gesturing: false,
                });

                if (snappedScale <= 1.02) {
                  this.resetZoomElement(pinchEl);
                  this.zoomGestureEl = null;
                  this.syncSplideDragToZoom();
                } else if (e.touches.length === 1) {
                  const t = e.touches[0];
                  this.zoomGestureEl = pinchEl;
                  this.touchPanSession = {
                    element: pinchEl,
                    startX: t.clientX,
                    startY: t.clientY,
                    startPanX: pinchEl.bbZoomPan?.x || 0,
                    startPanY: pinchEl.bbZoomPan?.y || 0,
                    moved: false,
                  };
                  handedOffToPan = true;
                  this.syncSplideDragToZoom();
                } else {
                  this.syncSplideDragToZoom();
                }
              }

              if (this.touchPanSession?.moved && e.touches.length === 0) {
                const panEl = this.touchPanSession.element;
                this.touchPanSession = null;
                panEl?.classList.remove("pdp-grid-gallery__zoom--panning");
                const image = panEl?.querySelector(".pdp-grid-gallery__image");
                if (panEl && image) this.setGestureState(panEl, image, false);
                this.lastZoomTouchEnd = 0;
                this.syncSplideDragToZoom();
                return;
              }

              if (!handedOffToPan && e.touches.length === 0) {
                this.touchPanSession = null;
              }

              if (e.touches.length > 0 || !element) return;

              const touch = e.changedTouches[0];
              const now = Date.now();
              const interval = now - this.lastZoomTouchEnd;
              const sameSpot =
                Math.abs(touch.clientX - this.lastZoomTouchX) < 40 &&
                Math.abs(touch.clientY - this.lastZoomTouchY) < 40;
              const isDoubleTap =
                this.lastZoomTouchEnd > 0 && interval > 0 && interval < 320 && sameSpot;

              if (isDoubleTap) {
                this.blockSplideTouch(e);
                this.lastZoomTouchEnd = 0;
                const maxScale = this.getZoomMaxScale(element);
                if (this.getZoomScale(element) > 1.02) {
                  this.resetZoomElement(element);
                  this.zoomGestureEl = null;
                  this.syncSplideDragToZoom();
                } else {
                  this.clearInlineZoom();
                  const pan = this.focalPanFromPoint(
                    touch.clientX,
                    touch.clientY,
                    element,
                    maxScale
                  );
                  setZoomState(element, maxScale, pan);
                }
                return;
              }

              this.lastZoomTouchEnd = now;
              this.lastZoomTouchX = touch.clientX;
              this.lastZoomTouchY = touch.clientY;

              if (this.getZoomScale(element) <= 1.02) {
                this.zoomGestureEl = null;
              }
              this.syncSplideDragToZoom();
            },
            { signal, passive: false, capture: true }
          );

          this.viewport.addEventListener(
            "touchcancel",
            (e) => {
              if (!this.isMobileCarousel()) return;
              if (this.pinchSession || this.touchPanSession || this.isZoomInteractionLocked()) {
                this.blockSplideTouch(e);
              }

              const element =
                this.pinchSession?.element || this.touchPanSession?.element || this.zoomGestureEl;
              this.pinchSession = null;
              this.touchPanSession = null;

              if (element) {
                element.classList.remove("pdp-grid-gallery__zoom--panning");
                const image = element.querySelector(".pdp-grid-gallery__image");
                if (image) this.setGestureState(element, image, false);

                if (this.snapScaleIfNearRest(this.getZoomScale(element)) <= 1) {
                  this.resetZoomElement(element);
                  this.zoomGestureEl = null;
                }
              }

              this.syncSplideDragToZoom();
            },
            { signal, passive: false, capture: true }
          );
        }

        document.addEventListener(
          "keydown",
          (e) => {
            if (e.key === "Escape") this.clearInlineZoom();
          },
          { signal }
        );
      }

      initForViewport() {
        if (this.isDesktop()) {
          this.destroySplide();
          if (this.viewport) {
            this.viewport.classList.add("is-rendered");
            this.viewport.classList.remove("is-initialized", "is-active");
          }
          return;
        }

        if (this.viewport) {
          this.viewport.classList.remove("is-rendered");
        }
        this.mountSplide();
      }

      mountSplide() {
        if (!this.viewport) return;

        if (typeof Splide === "undefined") {
          window.setTimeout(() => this.mountSplide(), 50);
          return;
        }

        this.destroySplide();

        this.splide = new Splide(this.viewport, {
          type: "slide",
          perPage: 1,
          perMove: 1,
          gap: "2px",
          padding: { right: "12%" },
          arrows: false,
          pagination: false,
          drag: true,
          speed: 400,
          rewind: false,
          focus: 0,
          trimSpace: false,
          updateOnMove: true,
        });

        this.splide.on("move", () => this.clearInlineZoom());
        this.splide.mount();
      }

      destroySplide() {
        if (this.splide) {
          this.splide.destroy(true);
          this.splide = null;
        }
      }

      toggleVideo(wrapper) {
        const video = wrapper.querySelector("video");
        const item = wrapper.closest(".pdp-grid-gallery__item--video");
        if (!video) return;

        if (video.paused) {
          video.play();
          if (item) item.classList.add("is-playing");
        } else {
          video.pause();
          if (item) item.classList.remove("is-playing");
        }
      }
    }
  );
}

/* Grid-gallery only: star ratings scroll to bottom reviews instead of Yotpo overview */
(function initGridGalleryReviewStarsScroll() {
  if (!document.querySelector(".product--grid-gallery")) return;

  let activeSession = null;

  function getReviewsTarget() {
    return (
      document.querySelector("#shopify-section-b84663d6866efa769f88") ||
      document.querySelector(".yotpo-reviews-main-widget") ||
      document.querySelector(
        '[data-yotpo-instance-id="475921"], [data-yotpo-instance-id="547710"], [data-yotpo-instance-id="545985"]'
      )
    );
  }

  function getHeaderScrollOffset() {
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--header-height")
    );
    return (Number.isFinite(headerHeight) ? headerHeight : 0) + 20;
  }

  function getTargetTop(target) {
    return target.getBoundingClientRect().top + window.scrollY - getHeaderScrollOffset();
  }

  function getNostoSectionsBeforeReviews(reviewsSection) {
    if (!reviewsSection) return [];

    const sections = [];
    let node = reviewsSection.previousElementSibling;

    while (node) {
      if (
        node.classList?.contains("shopify-section") &&
        (node.querySelector(".nosto_element") || node.querySelector("[id*='nosto']"))
      ) {
        sections.push(node);
      }
      node = node.previousElementSibling;
    }

    return sections;
  }

  function preloadNostoPlacements() {
    if (typeof window.nostojs !== "function") return;

    try {
      window.nostojs((api) => {
        if (typeof api.loadRecommendations === "function") {
          api.loadRecommendations();
        }
      });
    } catch (error) {
      // Ignore — scroll still works without an early Nosto load.
    }
  }

  function stopSession() {
    if (!activeSession) return;
    activeSession.cleanup();
    activeSession = null;
  }

  function scrollToReviews() {
    const scrollTarget = getReviewsTarget();
    if (!scrollTarget) return;

    stopSession();
    preloadNostoPlacements();

    const timers = [];
    let resizeObserver = null;
    let debounceTimer = null;
    let allowCorrections = false;

    const cleanup = () => {
      timers.forEach((id) => clearTimeout(id));
      if (debounceTimer) clearTimeout(debounceTimer);
      resizeObserver?.disconnect();
      if (activeSession?.cleanup === cleanup) activeSession = null;
    };

    const scrollToTarget = (behavior) => {
      window.scrollTo({
        top: Math.max(0, getTargetTop(scrollTarget)),
        behavior,
      });
    };

    const correctIfDrifted = () => {
      if (!allowCorrections) return;

      const offset = getHeaderScrollOffset();
      const drift = Math.abs(scrollTarget.getBoundingClientRect().top - offset);
      // Ignore tiny shifts — only re-lock when Nosto pushed the section meaningfully.
      if (drift < 80) return;

      scrollToTarget("auto");
    };

    const queueCorrection = () => {
      if (!allowCorrections) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(correctIfDrifted, 180);
    };

    activeSession = { cleanup };

    // One smooth scroll first — no competing corrections during the animation.
    scrollToTarget("smooth");

    // After the smooth scroll settles, quietly watch Nosto growth and correct only large drift.
    timers.push(
      setTimeout(() => {
        allowCorrections = true;
        correctIfDrifted();

        const nostoSections = getNostoSectionsBeforeReviews(
          scrollTarget.closest(".shopify-section") || scrollTarget
        );

        if (typeof ResizeObserver !== "undefined" && nostoSections.length) {
          resizeObserver = new ResizeObserver(queueCorrection);
          nostoSections.forEach((node) => resizeObserver.observe(node));
        }
      }, 700)
    );

    timers.push(setTimeout(cleanup, 2800));
  }

  function isTopReviewStarsTrigger(target) {
    const trigger = target?.closest?.(
      ".product__review, .yotpo-sr-bottom-line-summary, .yotpo-reviews-star-ratings-widget"
    );
    if (!trigger) return null;
    const info = document.querySelector(".product--grid-gallery .product__info-container");
    if (!info || !info.contains(trigger)) return null;
    return trigger;
  }

  document.addEventListener(
    "click",
    (event) => {
      const trigger = isTopReviewStarsTrigger(event.target);
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      scrollToReviews();
    },
    true
  );

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const trigger = isTopReviewStarsTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    scrollToReviews();
  });
})();

/* Complete the Look: Add to bag first, then sizes */
(function initCompleteTheLookAtc() {
  if (!document.querySelector(".product--grid-gallery")) return;

  const root = document.querySelector(".pdp-shop-the-look-below");
  if (!root) return;

  function setOpen(atc, open) {
    if (!atc) return;
    atc.classList.toggle("is-open", open);
    const trigger = atc.querySelector(".ctl-atc__trigger");
    if (trigger) trigger.setAttribute("aria-expanded", open ? "true" : "false");
  }

  function closeAll(except) {
    root.querySelectorAll(".ctl-atc.is-open").forEach((el) => {
      if (el !== except) setOpen(el, false);
    });
  }

  root.addEventListener("click", (event) => {
    const trigger = event.target.closest(".ctl-atc__trigger");
    if (!trigger || !root.contains(trigger)) return;
    event.preventDefault();
    event.stopPropagation();
    const atc = trigger.closest(".ctl-atc");
    const willOpen = !atc.classList.contains("is-open");
    closeAll(atc);
    setOpen(atc, willOpen);
  });

  document.addEventListener("click", (event) => {
    if (event.target.closest(".ctl-atc")) return;
    closeAll();
  });

  root.querySelectorAll(".product-card-image-wrapper").forEach((wrap) => {
    wrap.addEventListener("mouseleave", () => {
      if (!window.matchMedia("(hover: hover) and (min-width: 768px)").matches) return;
      setOpen(wrap.querySelector(".ctl-atc"), false);
    });
  });
})();

